import dayjs from 'dayjs';
import { ALLOWED_RFQ_ATTACHMENT_MIME, MAX_RFQ_ATTACHMENT_BYTES } from '../constants/rfqManagement';
import { readFileAsBase64 } from './vendorEntity';
import { getStoreActorId } from '../hooks/useStoreScope';
import { generateTxnNumber } from '../utils';

export function normalizeApiList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export function toFormDate(isoOrDate) {
  if (!isoOrDate) return '';
  return String(isoOrDate).includes('T')
    ? dayjs(isoOrDate).format('YYYY-MM-DD')
    : isoOrDate;
}

export function emptyRfqLine() {
  return {
    item_id: '',
    specification: '',
    required_quantity: '',
    uom_id: '',
    uom_code: '',
    required_by_date: '',
    remarks: '',
  };
}

export function emptyRfqAttachment() {
  return {
    file_name: '',
    mime_type: '',
    content_base64: '',
  };
}

/** API RFQResponse → UI-friendly shape */
export function fromApiRfq(r = {}) {
  const payload = r.rfq_payload && typeof r.rfq_payload === 'object' ? r.rfq_payload : {};
  return {
    id: r.id || r.rfq_id,
    rfq_id: r.id || r.rfq_id,
    rfq_number: r.rfq_number ?? '',
    title: r.rfq_title ?? r.title ?? '',
    rfq_title: r.rfq_title ?? r.title ?? '',
    rfq_type: r.rfq_type ?? 'STANDARD',
    status: r.status ?? 'DRAFT',
    issue_date: r.issue_date ?? '',
    deadline: r.submission_deadline ?? r.deadline ?? '',
    submission_deadline: r.submission_deadline ?? r.deadline ?? '',
    required_quotes_count: r.minimum_required_quotes ?? r.required_quotes_count ?? '',
    minimum_required_quotes: r.minimum_required_quotes ?? r.required_quotes_count ?? '',
    terms_and_conditions: r.terms_and_conditions ?? '',
    notes: r.notes ?? '',
    department_id: r.department_id ?? '',
    requested_by: r.requested_by ?? '',
    publish_at: r.publish_at ?? null,
    invited_vendor_ids: payload.invited_vendor_ids ?? r.vendor_ids ?? [],
    attachments: payload.attachments ?? {},
    rfq_payload: payload,
    is_active: r.is_active ?? true,
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

/** API RFQItemResponse → UI line */
export function fromApiRfqItem(item = {}) {
  return {
    id: item.id,
    item_id: item.item_id,
    required_quantity: item.requested_quantity ?? item.required_quantity ?? '',
    specification: item.specification ?? '',
    uom_id: item.uom_id ?? '',
    uom_code: item.uom_code ?? item.uom_name ?? '',
    required_by_date: item.required_by_date ?? '',
    remarks: item.remarks ?? '',
  };
}

export function toSubmissionDeadline(dateStr) {
  if (!dateStr) return undefined;
  if (dateStr.includes('T')) return dateStr;
  return dayjs(dateStr).hour(18).minute(0).second(0).millisecond(0).toISOString();
}

export function validateRfqAttachment(file) {
  if (!file) return 'No file selected';
  if (!ALLOWED_RFQ_ATTACHMENT_MIME.includes(file.type)) {
    return 'Allowed formats: PDF, JPG, PNG, DOC, DOCX';
  }
  if (file.size > MAX_RFQ_ATTACHMENT_BYTES) return 'File must be 5 MB or smaller';
  return null;
}

export async function fileToRfqAttachment(file) {
  const err = validateRfqAttachment(file);
  if (err) throw new Error(err);
  const content_base64 = await readFileAsBase64(file);
  return {
    file_name: file.name,
    mime_type: file.type,
    content_base64,
  };
}

export function attachmentsToPayload(rows) {
  const out = {};
  (rows || []).forEach((row, i) => {
    if (!row?.file_name || !row?.content_base64) return;
    const key = `ATTACHMENT_${i + 1}`;
    out[key] = {
      file_name: row.file_name,
      mime_type: row.mime_type,
      content_base64: row.content_base64,
    };
  });
  return out;
}

/** POST /rfqs body — spec #16 */
export function toRfqCreatePayload(data, { vendorIds = [], attachments = [] } = {}) {
  const actor = getStoreActorId();
  const attachmentMap = attachmentsToPayload(attachments);
  const rfq_payload = {
    ...(attachmentMap && Object.keys(attachmentMap).length ? { attachments: attachmentMap } : {}),
    ...(vendorIds?.length ? { invited_vendor_ids: vendorIds } : {}),
  };

  return {
    rfq_number: data.rfq_number?.trim(),
    rfq_title: data.rfq_title?.trim() || data.title?.trim(),
    rfq_type: data.rfq_type || 'STANDARD',
    requested_by: actor || undefined,
    department_id: data.department_id?.trim() || undefined,
    issue_date: data.issue_date || dayjs().format('YYYY-MM-DD'),
    submission_deadline: toSubmissionDeadline(data.submission_deadline || data.deadline),
    minimum_required_quotes: Number(data.minimum_required_quotes ?? data.required_quotes_count) || 1,
    terms_and_conditions: data.terms_and_conditions?.trim() || undefined,
    notes: data.notes?.trim() || undefined,
    rfq_payload: Object.keys(rfq_payload).length ? rfq_payload : {},
  };
}

/** PATCH /rfqs/{id} body — spec #19 */
export function toRfqUpdatePayload(data, { vendorIds = [], attachments = [], existingPayload = {} } = {}) {
  const attachmentMap = attachmentsToPayload(attachments);
  const rfq_payload = {
    ...existingPayload,
    ...(attachmentMap && Object.keys(attachmentMap).length ? { attachments: attachmentMap } : {}),
    invited_vendor_ids: vendorIds ?? existingPayload.invited_vendor_ids ?? [],
  };

  return {
    rfq_title: data.rfq_title?.trim() || data.title?.trim(),
    rfq_type: data.rfq_type || 'STANDARD',
    department_id: data.department_id?.trim() || undefined,
    issue_date: data.issue_date || undefined,
    submission_deadline: toSubmissionDeadline(data.submission_deadline || data.deadline),
    minimum_required_quotes: Number(data.minimum_required_quotes ?? data.required_quotes_count) || 1,
    terms_and_conditions: data.terms_and_conditions?.trim() || undefined,
    notes: data.notes?.trim() || undefined,
    rfq_payload,
  };
}

/** PATCH /rfq-items/{id} body */
export function toRfqItemUpdateBody(line) {
  const qty = line.required_quantity;
  return {
    requested_quantity: qty !== '' && qty != null ? String(qty) : undefined,
    uom_id: line.uom_id || undefined,
    required_by_date: line.required_by_date || undefined,
    specification: line.specification?.trim() || undefined,
    remarks: line.remarks?.trim() || undefined,
    item_payload: {},
  };
}

export function lineNeedsUpdate(original, edited) {
  return String(original.required_quantity) !== String(edited.required_quantity)
    || (original.uom_id || '') !== (edited.uom_id || '')
    || (original.required_by_date || '') !== (edited.required_by_date || '')
    || (original.specification || '').trim() !== (edited.specification || '').trim()
    || (original.remarks || '').trim() !== (edited.remarks || '').trim();
}

/** POST /vendor-quotes/{id}/evaluate body */
export function toEvaluateQuoteBody({ technical_score, commercial_score, evaluation_status, remarks }) {
  return {
    technical_score: String(technical_score),
    commercial_score: String(commercial_score),
    evaluation_status,
    remarks: remarks?.trim() || undefined,
  };
}

/** POST /vendor-quotes/{id}/accept body */
export function toAcceptQuoteBody({ remarks, reject_others = true } = {}) {
  const actor = getStoreActorId();
  return {
    acceptance_number: generateTxnNumber('ACC'),
    accepted_by: actor || undefined,
    valid_from: dayjs().format('YYYY-MM-DD'),
    valid_until: dayjs().add(6, 'month').format('YYYY-MM-DD'),
    remarks: remarks?.trim() || undefined,
    reject_others,
  };
}

/** POST /purchase-orders/from-accepted-quote body */
export function toPoFromQuoteBody(acceptedQuoteId, { po_number, po_date, expected_delivery_date, remarks } = {}) {
  return {
    po_number: po_number || generateTxnNumber('PO'),
    accepted_quote_id: acceptedQuoteId,
    po_date: po_date || dayjs().format('YYYY-MM-DD'),
    expected_delivery_date: expected_delivery_date || dayjs().add(30, 'day').format('YYYY-MM-DD'),
    priority: 'NORMAL',
    remarks: remarks?.trim() || undefined,
  };
}

export function canAcceptQuote(quote) {
  if (!quote) return false;
  const status = quote.status;
  const evalStatus = quote.evaluation_status;
  return status === 'EVALUATED'
    && ['RECOMMENDED', 'QUALIFIED'].includes(evalStatus);
}

export function validateRfqDates(issueDate, submissionDeadline) {
  if (!issueDate || !submissionDeadline) return null;
  if (dayjs(submissionDeadline).isBefore(dayjs(issueDate), 'day')) {
    return 'Submission deadline must be on or after issue date.';
  }
  return null;
}

/** POST /rfqs/{id}/items body — spec #20 */
export function toRfqApiItemBody(line, submissionDeadline) {
  const qty = line.required_quantity;
  return {
    item_id: line.item_id,
    requested_quantity: qty !== '' && qty != null ? String(qty) : undefined,
    uom_id: line.uom_id || undefined,
    required_by_date: line.required_by_date
      || (submissionDeadline ? dayjs(submissionDeadline).format('YYYY-MM-DD') : undefined),
    specification: line.specification?.trim() || undefined,
    remarks: line.remarks?.trim() || undefined,
    item_payload: {},
  };
}

export function getRfqWorkflowIndex(status) {
  const order = ['DRAFT', 'PUBLISHED', 'CLOSED', 'EVALUATED', 'AWARDED'];
  const normalized = status === 'OPEN' ? 'PUBLISHED' : status;
  const idx = order.indexOf(normalized);
  return idx >= 0 ? idx : 0;
}

export function isRfqPublished(status) {
  return status === 'PUBLISHED' || status === 'OPEN';
}

export function vendorDisplayLabel(v) {
  const name = v.vendor_name || v.name || 'Vendor';
  const code = v.vendor_code ? ` (${v.vendor_code})` : '';
  return `${name}${code}`;
}
